// HU-07 · Adjuntar evidencias. El control C-01 exige sustento: sin adjuntos no se envía.
//
// Cuatro pasos por archivo, y el ORDEN importa:
//
//   1. Validar contra los limites de HU-31. Antes de leer el archivo entero: no tiene
//      sentido calcular la huella de algo que se va a rechazar por tamano. El archivo
//      VACIO se rechaza aqui aunque la configuracion no haya cargado (BUG-380).
//   2. Calcular la huella SHA-256. Antes de subir, porque es lo que detecta el
//      duplicado — y subir dos veces el mismo archivo gasta almacenamiento y deja
//      dos blobs que nadie va a limpiar.
//   3. Pedir el permiso firmado y subir el binario (ADR-015: no pasa por la API).
//   4. Registrar la referencia con `POST /casos/{id}/adjuntos`.
//
// Si el 3 falla, el 4 NO se ejecuta: registrar la referencia de un blob que no existe
// deja el caso con una evidencia fantasma, y el control C-01 la daria por buena.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { Adjunto, ConfiguracionDeAdjuntos } from '../tipos';
import { asociarAdjunto, configuracionDeAdjuntos, quitarAdjunto } from '../api/casos';
import { pedirPermisoDeSubida, pedirPermisosDeSubidaEnLote, subirBinario } from '../api/almacenamiento';
import { aFallo } from '../api/cliente';
import { motivoDeArchivoVacio, motivosParaRechazar, sha256DeArchivo } from './huella';

export function useAdjuntos(casoId: string | null) {
  const [configuracion, setConfiguracion] = useState<ConfiguracionDeAdjuntos | null>(null);
  const [adjuntos, setAdjuntos] = useState<Adjunto[]>([]);
  const [subiendo, setSubiendo] = useState<string | null>(null);
  // La huella que se esta quitando AHORA. En estado para poder apagar el boton, y
  // ademas en un `ref` porque el estado llega tarde: dos clics seguidos ocurren
  // dentro del mismo ciclo y el segundo leeria todavia `null`.
  const [quitando, setQuitando] = useState<string | null>(null);
  const quitandoRef = useRef<string | null>(null);
  const [rechazos, setRechazos] = useState<string[]>([]);
  // Se guarda el error TAL CUAL, sin normalizar: la prueba de que un fallo del
  // servidor no saca la fila de la lista comprueba que siga siendo un `Error`. El hook
  // cuenta lo que paso; darle forma para la pantalla es cosa de la pantalla.
  const [fallo, setFallo] = useState<unknown>(null);
  // #377 · el 422 del lote («Elegiste 4 archivos y solo caben 2 (máximo 8 por caso)»): todo o
  // nada, no subió ninguno. Aparte de `fallo` porque es un aviso de cupo, no una subida caída.
  const [cupoExcedido, setCupoExcedido] = useState<string | null>(null);
  // #377-C1 · «6 MB de 100 MB», tal como lo dice el servidor en la respuesta del lote. Solo
  // existe tras un lote: al retomar o tras quitar una evidencia el servidor no lo devuelve
  // (vacío 3 de `ui/MG-ILE-377.md`) y la pantalla vuelve a su presupuesto de siempre.
  const [textoDelPresupuesto, setTextoDelPresupuesto] = useState<string | null>(null);

  useEffect(() => {
    let vigente = true;
    configuracionDeAdjuntos()
      .then((c) => { if (vigente) setConfiguracion(c as ConfiguracionDeAdjuntos); })
      .catch(() => {
        // Sin configuracion no se pre-valida, y ya esta: el servidor sigue validando.
        // Bloquear la seccion por no poder mostrar los limites seria desproporcionado.
      });
    return () => {
      vigente = false;
    };
  }, []);

  /**
   * HU-01 (retomar) · los adjuntos que el caso YA tenía, traídos del `GET /casos/{id}`.
   *
   * ⚠ ESTA LISTA NACIA VACIA SIEMPRE, y era el defecto que reporto el equipo el
   * 28-ago-2026 (T-44 §3): al retomar un borrador desde la bandeja, la seccion de
   * evidencias aparecia sin nada aunque el caso tuviera tres archivos subidos. El hook
   * solo conocia lo que se habia subido EN ESTA SESION, y de ahi salian dos danos:
   *
   *   · el registrador creia haber perdido los adjuntos y volvia a subirlos —el mismo
   *     binario, otra vez, ocupando almacenamiento y dejando dos filas—, porque la
   *     comprobacion de duplicado por huella tampoco los conocia;
   *   · el control C-01 contaba cero evidencias, asi que la seccion 6 pedia adjuntar
   *     al menos una en un caso que ya la tenia.
   *
   * QUIEN LLAMA A ESTO, Y POR QUE NO SE PIDE AQUI. La lista viene DENTRO del caso
   * (`CasoSalida.adjuntos`), y el camino de retomar ya hace ese `GET` para adoptar la
   * `version_bloqueo`. Pedirlo otra vez desde el hook seria una segunda peticion por el
   * mismo dato en el mismo instante — el "mecanismo paralelo" que T-44 pide no crear.
   * Quien tiene la respuesta la siembra aqui, en el mismo sitio donde adopta el id.
   *
   * FUSIONA POR HUELLA, NO PISA. La siembra y una subida pueden cruzarse —el `GET` del
   * retomar tarda lo que tarde, y entretanto se puede adjuntar—: con un reemplazo, una
   * respuesta que todavia no conoce el archivo recien subido lo borraria de la pantalla.
   * Fusionar por huella hace la siembra idempotente y sin orden: da igual cual llegue
   * antes, y sembrar dos veces no duplica ninguna fila.
   */
  const sembrar = useCallback((delServidor: Adjunto[] | null | undefined) => {
    if (!Array.isArray(delServidor) || !delServidor.length) return;
    setAdjuntos((previos) => {
      const conocidas = new Set(previos.map((a) => a.huella_sha256));
      const faltantes = delServidor.filter((a) => !conocidas.has(a.huella_sha256));
      return faltantes.length ? [...faltantes, ...previos] : previos;
    });
  }, []);

  /**
   * BUG-380 · el archivo de 0 bytes se rechaza EN EL NAVEGADOR, antes de cualquier
   * peticion y sin depender de la configuracion (AT S3 §2.2.7, criterio BUG-380-C1).
   *
   * Devuelve `true` si lo rechazo: el motivo queda en `rechazos` —el aviso «No se pudo
   * adjuntar» de la seccion 6— y se limpia un `fallo` anterior, porque un archivo vacio
   * no es un fallo de subida y «Falló la subida» no debe seguir a la vista.
   *
   * Es publico porque quien cablea la pantalla abre el borrador ANTES de llamar a
   * `agregar` (`RegistroConectado.alAdjuntar`): sin esta puerta previa, un formulario
   * nuevo abriria un caso en el servidor solo para rechazar un archivo vacio.
   */
  const rechazarSiEstaVacio = useCallback((archivo: File): boolean => {
    const motivo = motivoDeArchivoVacio(archivo);
    if (!motivo) return false;
    setFallo(null);
    setRechazos([motivo]);
    return true;
  }, []);

  const agregar = useCallback(
    async (archivo: File, { contieneDatoDePersonas = false }: { contieneDatoDePersonas?: boolean } = {}, idExplicito: string | null = null) => {
      setFallo(null);
      setRechazos([]);
      setCupoExcedido(null);

      // BUG-380 · el vacio primero: no necesita caso, ni configuracion, ni huella.
      if (rechazarSiEstaVacio(archivo)) return null;

      // El caso puede haberse creado en ESTE mismo clic: quien llama acaba de abrir el
      // borrador y nos pasa el id devuelto por el servidor. El `casoId` del estado aun
      // no ha llegado hasta aqui —el closure se creo con el valor anterior— asi que sin
      // esto la peticion saldria contra `/casos/null/...` y el servidor responderia 422.
      const caso = idExplicito ?? casoId;
      if (!caso) {
        setRechazos(['Todavía no hay un caso donde guardar la evidencia. Guarda el borrador primero.']);
        return null;
      }

      const motivos = motivosParaRechazar(archivo, configuracion, adjuntos);
      if (motivos.length) {
        setRechazos(motivos);
        return null;
      }

      setSubiendo(archivo.name);
      try {
        const huella = await sha256DeArchivo(archivo);

        // HU-07: "evita subir dos veces el mismo archivo al mismo caso". Se compara
        // por huella y no por nombre: "informe.pdf" se llama igual en todas partes.
        if (adjuntos.some((a) => a.huella_sha256 === huella)) {
          setRechazos(['Ese archivo ya está adjunto a este caso.']);
          return null;
        }

        // La `ruta_blob` la fija el SERVIDOR al firmar el permiso, y se devuelve
        // tal cual en el paso 3. Componerla tambien aqui daria dos sitios donde
        // decidir la misma ruta, y acabarian divergiendo.
        const permiso = await pedirPermisoDeSubida(caso, {
          nombreArchivo: archivo.name,
          // El servidor los exige aqui para aplicar los limites ANTES de firmar. No
          // cuestan un recorrido extra: la huella ya se calculo arriba para detectar el
          // duplicado, y el tamanio lo trae el propio `File`.
          huellaSha256: huella,
          tamanoBytes: archivo.size,
          contieneDatoDePersonas: contieneDatoDePersonas,
        });
        const rutaBlob = await subirBinario(archivo, permiso);
        const registrado = await asociarAdjunto(caso, {
          nombreArchivo: archivo.name,
          rutaBlob,
          huellaSha256: huella,
          tipoMime: archivo.type || 'application/octet-stream',
          tamanoBytes: archivo.size,
          // La fila tiene que decir lo mismo que el contenedor donde acabo el blob.
          contieneDatoDePersonas: contieneDatoDePersonas,
        });

        // `registrado` viene del API y puede traer campos que aqui no se enumeran
        // (el id, la fecha). Se declara su forma para poder esparcirlo: un `unknown`
        // no se puede esparcir, y con razon.
        // EL SPREAD VA PRIMERO. Estaba al final y pisaba los cuatro campos de abajo
        // con lo que devolviera el servidor; TypeScript lo senala como
        // "'huella_sha256' is specified more than once". Lo que se acaba de calcular
        // aqui -la huella con la que se subio el binario- es lo que tiene que quedar.
        const nuevo: Adjunto & { version_bloqueo?: number } = {
          ...(registrado as Adjunto),
          nombre_archivo: archivo.name,
          huella_sha256: huella,
          tamano_bytes: archivo.size,
          tipo_mime: archivo.type,
        };
        setAdjuntos((previos) => [...previos, nuevo]);
        return nuevo;
      } catch (error) {
        setFallo(error);
        return null;
      } finally {
        setSubiendo(null);
      }
    },
    [casoId, configuracion, adjuntos, rechazarSiEstaVacio],
  );


  /**
   * #377 · MG-ILE-377 · VARIOS ARCHIVOS EN UNA SOLA PETICIÓN (AT S3 §2.2.7).
   *
   * Los de 0 bytes se descartan aquí (BUG-380) y no viajan; los ya adjuntos (misma huella)
   * tampoco, con el aviso de siempre (vacío 5 de la spec). El resto va en UN lote: el servidor
   * aplica la lista negra, el tope por archivo y el cupo del caso, y responde qué entra
   * (`aceptados[]`, cada uno con SU URL), qué no y por qué (`rechazados[].texto`, tal cual) y
   * cuánto se lleva (`texto_del_presupuesto`). Si la selección no cabe, 422 y no sube ninguno
   * (todo o nada, C2). Cada aceptado sigue los pasos vigentes —subir y registrar—, y el fallo
   * de uno no deshace los otros (vacío 1 de la spec).
   *
   * Devuelve el último adjunto registrado (con su `version_bloqueo`) para que quien cablea la
   * pantalla adopte la versión, o `null` si no se registró ninguno.
   */
  const agregarVarios = useCallback(
    async (archivos: File[], { contieneDatoDePersonas = false }: { contieneDatoDePersonas?: boolean } = {}, idExplicito: string | null = null) => {
      setFallo(null);
      setRechazos([]);
      setCupoExcedido(null);

      const vacios = archivos.map(motivoDeArchivoVacio).filter((m): m is string => Boolean(m));
      const conContenido = archivos.filter((a) => !motivoDeArchivoVacio(a));
      if (!conContenido.length) {
        setRechazos(vacios);
        return null;
      }
      const caso = idExplicito ?? casoId;
      if (!caso) {
        setRechazos(['Todavía no hay un caso donde guardar la evidencia. Guarda el borrador primero.']);
        return null;
      }

      setSubiendo(conContenido.map((a) => a.name).join(', '));
      const avisos = [...vacios];
      let ultimo: (Adjunto & { version_bloqueo?: number }) | null = null;
      try {
        const conHuella = await Promise.all(conContenido.map(async (archivo) => ({ archivo, huella: await sha256DeArchivo(archivo) })));
        const yaAdjuntas = new Set(adjuntos.map((a) => a.huella_sha256));
        const nuevos = conHuella.filter(({ archivo, huella }) => {
          if (!yaAdjuntas.has(huella)) return true;
          avisos.push(`«${archivo.name}»: ese archivo ya está adjunto a este caso.`);
          return false;
        });
        if (!nuevos.length) {
          setRechazos(avisos);
          return null;
        }

        let lote;
        try {
          lote = await pedirPermisosDeSubidaEnLote(caso, {
            contieneDatoDePersonas,
            archivos: nuevos.map(({ archivo, huella }) => ({
              nombre_archivo: archivo.name, huella_sha256: huella, tamano_bytes: archivo.size,
            })),
          });
        } catch (error) {
          // C2: la selección no cabe. Es el texto del servidor (`detalle`), no el error crudo.
          const motivo = aFallo(error);
          if (motivo.estado === 422) {
            setCupoExcedido(motivo.message);
            setRechazos(avisos);
            return null;
          }
          throw error;
        }
        avisos.push(...lote.rechazados.map((r) => r.texto));
        setRechazos(avisos);
        setTextoDelPresupuesto(lote.texto_del_presupuesto || null);

        for (const permiso of lote.aceptados) {
          const elegido = nuevos.find(({ huella }) => huella === permiso.huella_sha256);
          if (!elegido) continue;
          try {
            const rutaBlob = await subirBinario(elegido.archivo, permiso);
            const registrado = await asociarAdjunto(caso, {
              nombreArchivo: elegido.archivo.name,
              rutaBlob,
              huellaSha256: elegido.huella,
              tipoMime: elegido.archivo.type || 'application/octet-stream',
              tamanoBytes: elegido.archivo.size,
              // C4: la marca vale para todo el lote, igual que en la URL.
              contieneDatoDePersonas,
            });
            const nuevo: Adjunto & { version_bloqueo?: number } = {
              ...(registrado as Adjunto),
              nombre_archivo: elegido.archivo.name,
              huella_sha256: elegido.huella,
              tamano_bytes: elegido.archivo.size,
              tipo_mime: elegido.archivo.type,
              contiene_dato_de_personas: contieneDatoDePersonas,
            };
            setAdjuntos((previos) => [...previos, nuevo]);
            ultimo = nuevo;
          } catch (error) {
            // Uno que falla no deshace los demás: se avisa y se sigue con el siguiente.
            setFallo(error);
          }
        }
        return ultimo;
      } catch (error) {
        setFallo(error);
        return ultimo;
      } finally {
        setSubiendo(null);
      }
    },
    [casoId, adjuntos],
  );

  /** #377 · si TODA la selección son archivos vacíos se rechaza sin abrir caso ni pedir nada (BUG-380). */
  const rechazarSiTodosEstanVacios = useCallback((archivos: File[]): boolean => {
    const motivos = archivos.map(motivoDeArchivoVacio);
    if (!archivos.length || motivos.some((m) => !m)) return false;
    setFallo(null);
    setCupoExcedido(null);
    setRechazos(motivos.filter((m): m is string => Boolean(m)));
    return true;
  }, []);

  /**
   * HU-07 · quitar una evidencia ya adjunta.
   *
   * Se identifica por HUELLA y no por posicion: la lista se reordena al retomar y un
   * indice apuntaria a otra fila. Es ademas lo que el endpoint espera.
   *
   * Optimista NO: se espera la respuesta del servidor antes de sacarla de la lista. Un
   * borrado que falla y deja la fila fuera de la pantalla hace creer al registrador que
   * el caso ya no tiene esa evidencia cuando si la tiene, y el control C-01 se evalua
   * sobre lo que hay en la base, no sobre lo que se ve.
   */
  const quitar = useCallback(
    async (huellaSha256: string): Promise<{ version_bloqueo?: number } | null> => {
      if (!casoId || !huellaSha256) return null;
      // ⚠ UNA SOLA VEZ (reportado por el operador, 9-sep-2026). El boton del tacho no se
      // apagaba, asi que tres clics disparaban tres DELETE. El primero borra; los otros
      // dos no encuentran nada que borrar, pero CADA UNO vuelve a guardar el caso y el
      // trigger de la base sube `version_bloqueo`. De 12 a 15 en tres clics — y el
      // autoguardado, que seguia con la 12, recibia un 409 al siguiente PUT.
      if (quitandoRef.current) return null;
      quitandoRef.current = huellaSha256;
      setQuitando(huellaSha256);
      setFallo(null);
      try {
        // ⚠ SE DEVUELVE LA RESPUESTA, no `true`. `DELETE /casos/{id}/adjuntos/{huella}`
        // responde el BORRADOR ENTERO con su `version_bloqueo` —lo dice su propia
        // documentacion: "el cuerpo es el mismo que devuelven adjuntar y guardar, asi que
        // el cliente refresca por el camino de siempre"— y aqui se tiraba. Quien llama la
        // necesita para adoptar la version nueva; sin eso el desfase es de una version
        // por borrado.
        const borrador = await quitarAdjunto(casoId, huellaSha256);
        setAdjuntos((previos) => previos.filter((a) => a.huella_sha256 !== huellaSha256));
        // El «6 MB de 100 MB» del último lote ya no es cierto: sin texto del servidor, la
        // pantalla vuelve a su presupuesto (vacío 3 de `ui/MG-ILE-377.md`).
        setTextoDelPresupuesto(null);
        return borrador ?? null;
      } catch (error) {
        setFallo(error);
        return null;
      } finally {
        quitandoRef.current = null;
        setQuitando(null);
      }
    },
    [casoId],
  );

  return {
    configuracion, adjuntos, subiendo, quitando, rechazos, fallo, agregar, quitar, sembrar,
    rechazarSiEstaVacio,
    // #377
    agregarVarios, rechazarSiTodosEstanVacios, cupoExcedido, textoDelPresupuesto,
    limpiarRechazos: () => setRechazos([]),
  };
}
